import { useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { userService } from '@/services/api'
import { Shield, UserPlus, Edit, Trash2, Search, KeyRound } from 'lucide-react'
import UserModal from '@/components/users/UserModal'
import Modal from '@/components/common/Modal'
import { TextInput } from '@/components/common/FormField'
import toast from 'react-hot-toast'

const ROLE_LABELS = {
  super_admin: 'Супер Администратор',
  admin: 'Администратор',
  department_admin: 'Администратор кафедры',
  hr: 'HR',
  viewer: 'Просмотр',
  operator: 'Оператор СКУД',
}

const ROLE_BADGE = {
  super_admin: 'badge-red',
  admin: 'badge-purple',
  department_admin: 'badge-blue',
  hr: 'badge-yellow',
  viewer: 'badge-gray',
  operator: 'badge-green',
}

export default function UsersPage() {
  const qc = useQueryClient()
  const [search, setSearch] = useState('')
  const [modal, setModal] = useState({ open: false, data: null })
  const [deleteModal, setDeleteModal] = useState({ open: false, id: null, name: '' })
  const [pwModal, setPwModal] = useState({ open: false, id: null, name: '' })
  const [newPassword, setNewPassword] = useState('')
  const [deleting, setDeleting] = useState(false)
  const [resetting, setResetting] = useState(false)

  const { data, isLoading } = useQuery({
    queryKey: ['system-users', search],
    queryFn: () => userService.list({ search: search || undefined }).then((r) => r.data.results || r.data),
  })

  const handleDelete = async () => {
    setDeleting(true)
    try {
      await userService.delete(deleteModal.id)
      toast.success('Пользователь удалён')
      qc.invalidateQueries(['system-users'])
      setDeleteModal({ open: false, id: null, name: '' })
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Ошибка удаления')
    } finally {
      setDeleting(false)
    }
  }

  const handleResetPassword = async () => {
    if (!newPassword.trim() || newPassword.length < 8) {
      toast.error('Минимум 8 символов')
      return
    }
    setResetting(true)
    try {
      await userService.resetPassword(pwModal.id, { password: newPassword })
      toast.success('Пароль изменён')
      setPwModal({ open: false, id: null, name: '' })
      setNewPassword('')
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Ошибка смены пароля')
    } finally {
      setResetting(false)
    }
  }

  const users = data || []

  return (
    <div className="space-y-5">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Пользователи системы</h1>
          {!isLoading && (
            <p className="text-sm text-gray-400 mt-0.5">{users.length} пользователей</p>
          )}
        </div>
        <button className="btn-primary self-start sm:self-auto" onClick={() => setModal({ open: true, data: null })}>
          <UserPlus size={16} /> <span className="hidden sm:inline">Добавить пользователя</span><span className="sm:hidden">Добавить</span>
        </button>
      </div>

      {/* Search */}
      <div className="card">
        <div className="relative">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            className="input pl-9 w-full sm:max-w-sm"
            placeholder="Поиск по имени или логину..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
        {/* Mobile card list */}
        <div className="md:hidden divide-y divide-gray-100">
          {isLoading ? (
            Array(4).fill(0).map((_, i) => (
              <div key={i} className="p-4 animate-pulse flex gap-3">
                <div className="w-10 h-10 bg-gray-100 rounded-full shrink-0" />
                <div className="flex-1 space-y-2">
                  <div className="h-4 bg-gray-100 rounded w-2/3" />
                  <div className="h-3 bg-gray-100 rounded w-1/3" />
                </div>
              </div>
            ))
          ) : users.length === 0 ? (
            <div className="py-12 text-center text-gray-400">
              <Shield size={32} className="mx-auto mb-2 opacity-30" />
              <p>Пользователей не найдено</p>
            </div>
          ) : (
            users.map((u) => {
              const roleName = u.role_detail?.name || u.role
              return (
                <div key={u.id} className="p-4 flex items-center gap-3">
                  <div className="w-10 h-10 bg-primary-100 text-primary-600 rounded-full flex items-center justify-center text-sm font-bold shrink-0">
                    {u.full_name?.[0] || '?'}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-gray-900 truncate">{u.full_name}</p>
                    <p className="text-xs text-gray-400 font-mono truncate">{u.username}</p>
                    <div className="flex gap-1 mt-1 flex-wrap">
                      {roleName && <span className={`flex items-center gap-1 ${ROLE_BADGE[roleName] || 'badge-gray'}`}><Shield size={11} />{ROLE_LABELS[roleName] || roleName}</span>}
                      {u.is_active ? <span className="badge-green">Активен</span> : <span className="badge-red">Отключён</span>}
                    </div>
                  </div>
                  <div className="flex gap-1 shrink-0">
                    <button className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded" onClick={() => setModal({ open: true, data: u })}><Edit size={14} /></button>
                    <button className="p-1.5 text-gray-400 hover:text-amber-600 hover:bg-amber-50 rounded" onClick={() => { setPwModal({ open: true, id: u.id, name: u.full_name }); setNewPassword('') }}><KeyRound size={14} /></button>
                    <button className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded" onClick={() => setDeleteModal({ open: true, id: u.id, name: u.full_name })}><Trash2 size={14} /></button>
                  </div>
                </div>
              )
            })
          )}
        </div>

        {/* Desktop table */}
        <div className="hidden md:block overflow-x-auto">
          <table>
            <thead>
              <tr>
                <th>ФИО</th>
                <th>Логин</th>
                <th>Роль</th>
                <th>Кафедра / Отдел</th>
                <th>Email</th>
                <th>Статус</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                Array(4).fill(0).map((_, i) => (
                  <tr key={i}>
                    {Array(7).fill(0).map((_, j) => (
                      <td key={j}><div className="h-4 bg-gray-100 rounded animate-pulse" /></td>
                    ))}
                  </tr>
                ))
              ) : users.length === 0 ? (
                <tr>
                  <td colSpan={7} className="text-center py-12 text-gray-400">
                    <Shield size={32} className="mx-auto mb-2 opacity-30" />
                    <p>Пользователей не найдено</p>
                  </td>
                </tr>
              ) : (
                users.map((u) => {
                  const roleName = u.role_detail?.name || u.role
                  return (
                    <tr key={u.id}>
                      <td>
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 bg-primary-100 text-primary-600 rounded-full flex items-center justify-center text-sm font-bold">
                            {u.full_name?.[0] || '?'}
                          </div>
                          <span className="font-medium">{u.full_name}</span>
                        </div>
                      </td>
                      <td className="font-mono text-sm text-gray-600">{u.username}</td>
                      <td>
                        {roleName ? (
                          <span className={`flex items-center gap-1 ${ROLE_BADGE[roleName] || 'badge-gray'}`}>
                            <Shield size={11} />
                            {ROLE_LABELS[roleName] || roleName}
                          </span>
                        ) : (
                          <span className="badge-gray">Нет роли</span>
                        )}
                      </td>
                      <td className="text-gray-500 text-sm">
                        {u.department_name || '—'}
                      </td>
                      <td className="text-gray-500 text-sm">{u.email || '—'}</td>
                      <td>
                        {u.is_active
                          ? <span className="badge-green">Активен</span>
                          : <span className="badge-red">Отключён</span>}
                      </td>
                      <td>
                        <div className="flex gap-1">
                          <button
                            className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors"
                            onClick={() => setModal({ open: true, data: u })}
                            title="Редактировать"
                          >
                            <Edit size={14} />
                          </button>
                          <button
                            className="p-1.5 text-gray-400 hover:text-amber-600 hover:bg-amber-50 rounded transition-colors"
                            onClick={() => { setPwModal({ open: true, id: u.id, name: u.full_name }); setNewPassword('') }}
                            title="Сменить пароль"
                          >
                            <KeyRound size={14} />
                          </button>
                          <button
                            className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                            onClick={() => setDeleteModal({ open: true, id: u.id, name: u.full_name })}
                            title="Удалить"
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  )
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* User Modal */}
      <UserModal
        open={modal.open}
        onClose={() => setModal({ open: false, data: null })}
        user={modal.data}
      />

      {/* Reset Password Modal */}
      <Modal
        open={pwModal.open}
        onClose={() => !resetting && setPwModal({ open: false, id: null, name: '' })}
        title="Смена пароля"
        size="sm"
        footer={
          <>
            <button
              className="btn-secondary"
              onClick={() => setPwModal({ open: false, id: null, name: '' })}
              disabled={resetting}
            >
              Отмена
            </button>
            <button className="btn-primary" onClick={handleResetPassword} disabled={resetting}>
              {resetting ? 'Сохранение...' : 'Сохранить'}
            </button>
          </>
        }
      >
        <p className="text-sm text-gray-500 mb-4">
          Новый пароль для пользователя <strong>{pwModal.name}</strong>
        </p>
        <TextInput
          label="Новый пароль"
          type="password"
          value={newPassword}
          onChange={(e) => setNewPassword(e.target.value)}
          placeholder="Минимум 8 символов"
        />
      </Modal>

      {/* Delete Confirm */}
      <Modal
        open={deleteModal.open}
        onClose={() => !deleting && setDeleteModal({ open: false, id: null, name: '' })}
        title="Удаление пользователя"
        size="sm"
        footer={
          <>
            <button
              className="btn-secondary"
              onClick={() => setDeleteModal({ open: false, id: null, name: '' })}
              disabled={deleting}
            >
              Отмена
            </button>
            <button className="btn-danger" onClick={handleDelete} disabled={deleting}>
              {deleting ? 'Удаление...' : 'Удалить'}
            </button>
          </>
        }
      >
        <p className="text-gray-600">
          Удалить пользователя <strong>«{deleteModal.name}»</strong>?
        </p>
        <p className="text-sm text-gray-400 mt-2">
          Это действие необратимо. Логи активности сохранятся.
        </p>
      </Modal>
    </div>
  )
}
